import { useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { PasskeysService } from '../services/passkeysService';
import type { User } from '@supabase/supabase-js';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
      } else {
        // Check for passkey session in localStorage
        const passkeySession = localStorage.getItem('supabase.auth.token');
        if (passkeySession) {
          try {
            const sessionData = JSON.parse(passkeySession);
            if (sessionData.user && sessionData.expires_at > Math.floor(Date.now() / 1000)) {
              setUser(sessionData.user);
            } else {
              // Session expired, remove it
              localStorage.removeItem('supabase.auth.token');
            }
          } catch (error) {
            console.error('Error parsing passkey session:', error);
            localStorage.removeItem('supabase.auth.token');
          }
        }
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          setUser(session.user);
          // Clear any passkey session when Supabase session is active
          localStorage.removeItem('supabase.auth.token');
        } else {
          // Check for passkey session when Supabase session is cleared
          const passkeySession = localStorage.getItem('supabase.auth.token');
          if (passkeySession) {
            try {
              const sessionData = JSON.parse(passkeySession);
              if (sessionData.user && sessionData.expires_at > Math.floor(Date.now() / 1000)) {
                setUser(sessionData.user);
              } else {
                localStorage.removeItem('supabase.auth.token');
                setUser(null);
              }
            } catch (error) {
              localStorage.removeItem('supabase.auth.token');
              setUser(null);
            }
          } else {
            setUser(null);
          }
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Listen for passkey authentication success
  useEffect(() => {
    const handlePasskeyAuthSuccess = (e: CustomEvent) => {
      if (e.detail?.user) {
        setUser(e.detail.user);
      }
    };

    window.addEventListener('passkey-auth-success', handlePasskeyAuthSuccess as EventListener);
    return () => window.removeEventListener('passkey-auth-success', handlePasskeyAuthSuccess as EventListener);
  }, []);

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    });
    if (error) throw error;
  };

  const signInWithPasskey = async () => {
    try {
      // Check if passkeys are supported
      if (!PasskeysService.isSupported()) {
        throw new Error('Passkeys are not supported on this device');
      }

      // Try to authenticate with passkey
      const result = await PasskeysService.authenticateWithPasskey();
      
      if (!result.success || !result.userId) {
        throw new Error(result.error || 'Passkey authentication failed');
      }

      // Get the user's profile to find their email
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', result.userId)
        .single();

      if (profileError || !profile?.email) {
        throw new Error('User profile not found');
      }

      // Since Supabase doesn't have native passkey support, we need to use a workaround
      // We'll create a custom session by calling a Supabase RPC function
      // This requires setting up a server-side function to handle passkey verification
      
      // For now, let's use a simpler approach - we'll store the passkey-authenticated user
      // in localStorage and create a custom session
      const customSession = {
        user: {
          id: result.userId,
          email: profile.email,
          aud: 'authenticated',
          role: 'authenticated',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          app_metadata: {},
          user_metadata: {},
          identities: []
        },
        access_token: 'passkey-auth-token', // This would be a real JWT in production
        refresh_token: 'passkey-refresh-token',
        expires_in: 3600,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        token_type: 'bearer'
      };

      // Store the session in localStorage
      localStorage.setItem('supabase.auth.token', JSON.stringify(customSession));
      
      // Dispatch a custom event to notify other components
      window.dispatchEvent(new CustomEvent('passkey-auth-success', { 
        detail: { user: customSession.user } 
      }));
      
      // Update the user state
      setUser(customSession.user as User);
      
      return customSession;
    } catch (error: any) {
      throw error;
    }
  };

  const signOut = async () => {
    // Clear passkey session
    localStorage.removeItem('supabase.auth.token');
    
    // Sign out from Supabase
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return { user, loading, signUp, signIn, signInWithGoogle, signInWithPasskey, signOut };
};
